import React, { useEffect, useState } from 'react';
import { 
  Form, 
  Button, 
  Row, 
  Col, 
  Alert, 
  Card,
  Spinner,
  InputGroup,
  FloatingLabel
} from 'react-bootstrap';
import { FaUserCircle } from 'react-icons/fa';
import { TbEye, TbEyeOff } from "react-icons/tb";
import { useTheme } from "../ThemeContext";
import { useUiVersion } from "../UiVersionContext";
import { apifetch } from '../services/interceptors';
import TwoFactorSettings from './TwoFactorSettings';

// Opt in to the redesigned interface. The switch also lives in the new shell's top
// bar so nobody has to come back here to leave; this is the place to *find* it, not the
// only way out of it.
const InterfaceSetting = () => {
  const { isDarkMode } = useTheme();
  const { uiVersion, setUiVersion, isUrlOverridden } = useUiVersion();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const toggle = async () => {
    setSaving(true);
    setError("");
    const ok = await setUiVersion(uiVersion === "v2" ? "v1" : "v2");
    if (!ok) setError("Could not save that. Your interface has been left as it was.");
    setSaving(false);
  };

  return (
    <div className="mt-4 pt-4 border-top">
      <h5 className="mb-1">Interface</h5>
      <p className={isDarkMode ? "text-gray small" : "text-muted small"}>
        The redesigned interface reorganises navigation. Your data and every screen stay
        the same, and you can switch back at any time.
      </p>

      {isUrlOverridden && (
        <Alert variant="warning" className="py-2 small">
          This page is showing a specific interface because of the <code>?ui=</code> value
          in the address bar. Remove it to return to your saved preference.
        </Alert>
      )}

      <Form.Check
        type="switch"
        id="ui-version-switch"
        label={uiVersion === "v2" ? "Using the new interface" : "Use the new interface"}
        checked={uiVersion === "v2"}
        disabled={saving || isUrlOverridden}
        onChange={toggle}
      />

      {error && <div className="text-danger small mt-2">{error}</div>}
    </div>
  );
};

const Profile = ({ username, email }) => {
  const [showOldPass, setShowOldPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const { isDarkMode } = useTheme();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // State to store the user's profile data
  const [profileData, setProfileData] = useState({
    name: username,
    email: email,
    password: '',
    confirmPassword: '',
    oldPassword: ''
  });

  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const themeStyles = {
    backgroundColor: isDarkMode ? "#121212" : "#f5f7fa",
    color: isDarkMode ? "#ffffff" : "#212529",
    minHeight: "100vh",
    paddingBottom: "50px"
  };

  const cardThemeStyles = {
    backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
    color: isDarkMode ? "#ffffff" : "#212529",
    border: 'none',
    borderRadius: '12px',
    boxShadow: isDarkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.05)'
  };

  const inputThemeStyles = {
    backgroundColor: isDarkMode ? '#2a2a2a' : '#fff',
    color: isDarkMode ? '#ffffff' : '#212529',
    borderColor: isDarkMode ? '#444' : '#ced4da',
    height: "58px" // keep inputs & icons aligned
  };

  const handleChange = (e) => {
    e.preventDefault();
    const { name, value } = e.target;

    setProfileData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Basic validation
    if (profileData.password !== profileData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');

      const response = await apifetch('s1/api/update', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          "email": profileData.email,
          "password": profileData.password,
          "oldPassword": profileData.oldPassword
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setError(data.error || "An unexpected error occurred");
        setIsSuccess(false);
      } else {
        setMessage(data.message || "Profile updated successfully!");
        setIsSuccess(true);
        
        // Clear password fields on success
        setProfileData(prev => ({
          ...prev,
          password: '',
          confirmPassword: '',
          oldPassword: ''
        }));
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Something is wrong please try again later.');
      setIsSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setError('');
      setMessage('');
    }, 4000);
    
    return () => clearTimeout(timer);
  }, [error, message]);

  return (
    <div className="container-fluid py-4" style={themeStyles}>
      <Row className="justify-content-center">
        <Col xl={6} lg={8} md={10}>
          {/* Header Section */}
          <div className="text-center mb-4">
            <FaUserCircle size={80} className="text-primary mb-3" />
            <h2>User Profile</h2>
            <p className="">Manage your account settings</p>
          </div>


          {/* Profile Card */}
          <Card style={cardThemeStyles}>
            <Card.Body className="p-4">
              <Form onSubmit={handleSubmit}>
                <Row className="mb-4">
                  <Col md={12}>
                    <FloatingLabel label="Name" className="mb-3">
                      <Form.Control
                        type="text"
                        placeholder=" "
                        name="name"
                        value={profileData.name}
                        onChange={handleChange}
                        style={inputThemeStyles}
                        readOnly
                      />
                    </FloatingLabel>
                  </Col>
                  <Col md={12}>
                    <FloatingLabel label="Email Address" className="mb-3">
                      <Form.Control
                        type="email"
                        placeholder=" "
                        name="email"
                        value={profileData.email}
                        onChange={handleChange}
                        style={inputThemeStyles}
                        readOnly
                      />
                    </FloatingLabel>
                  </Col>
                </Row>

                <h5 className="mb-3 border-bottom pb-2">Change Password</h5>
                
                {/* Old Password */}
                <FloatingLabel 
                  label="Current Password" 
                  className={`mb-3 ${profileData.oldPassword ? "filled" : ""}`}
                >
                  <InputGroup>
                    <Form.Control
                      required
                      type={showOldPass ? "text" : "password"}
                      placeholder=" "
                      name="oldPassword"
                      value={profileData.oldPassword}
                      onChange={handleChange}
                      style={inputThemeStyles}
                    />
                    <InputGroup.Text
                      style={inputThemeStyles}
                      role="button"
                      tabIndex={0}
                      onClick={() => setShowOldPass(!showOldPass)}
                    >
                      {showOldPass ? <TbEyeOff /> : <TbEye />}
                    </InputGroup.Text>
                  </InputGroup>
                </FloatingLabel>

                {/* New Password */}
                <FloatingLabel 
                  label="New Password" 
                  className={`mb-3 ${profileData.password ? "filled" : ""}`}
                >
                  <InputGroup>
                    <Form.Control
                      required
                      type={showNewPass ? "text" : "password"}
                      placeholder=" "
                      name="password"
                      value={profileData.password}
                      onChange={handleChange}
                      style={inputThemeStyles}
                    />
                    <InputGroup.Text
                      style={inputThemeStyles}
                      role="button"
                      tabIndex={0}
                      onClick={() => setShowNewPass(!showNewPass)}
                    >
                      {showNewPass ? <TbEyeOff /> : <TbEye />}
                    </InputGroup.Text>
                  </InputGroup>
                </FloatingLabel>

                {/* Confirm Password */}
                <FloatingLabel 
                  label="Confirm New Password" 
                  className={`mb-4 ${profileData.confirmPassword ? "filled" : ""}`}
                >
                  <InputGroup>
                    <Form.Control
                      required
                      type={showConfirmPass ? "text" : "password"}
                      placeholder=" "
                      name="confirmPassword"
                      value={profileData.confirmPassword}
                      onChange={handleChange}
                      style={inputThemeStyles}
                    />
                    <InputGroup.Text
                      style={inputThemeStyles}
                      role="button"
                      tabIndex={0}
                      onClick={() => setShowConfirmPass(!showConfirmPass)}
                    >
                      {showConfirmPass ? <TbEyeOff /> : <TbEye />}
                    </InputGroup.Text>
                  </InputGroup>
                </FloatingLabel>
                {/* Alerts */}
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError("")} className="mb-3">
              {error}
            </Alert>
          )}
          {message && (
            <Alert variant="success" dismissible onClose={() => setMessage("")} className="mb-3">
              {message}
            </Alert>
          )}

                <div className="d-grid">
                  <Button 
                    variant="primary" 
                    type="submit" 
                    size="lg"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Updating...
                      </>
                    ) : (
                      'Update Profile'
                    )}
                  </Button>
                </div>
              </Form>

              <TwoFactorSettings />

              <InterfaceSetting />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <style>
        {`
          .cursor-pointer {
            cursor: pointer;
          }
          /* Force floating label when field has content */
          .filled > label {
            opacity: 1 !important;
            transform: scale(.85) translateY(-0.6rem) translateX(0.15rem);
          }
          .input-group-text {
            display: flex;
            align-items: center;
            justify-content: center;
          }
        `}
      </style>
    </div>
  );
};

export default Profile;
